import { getApiBaseUrl } from "../config/apiBase";
import { apiFetch } from "../config/apiFetch";

async function parseError(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const j = JSON.parse(text) as { error?: string; message?: string };
    if (j.error) return j.error;
    if (j.message) return j.message;
  } catch { /* ignore */ }
  return text || `Request failed (${res.status})`;
}

// ── Types ────────────────────────────────────────────────────────────────────

export interface ProjectSchedule {
  id: string;
  name: string;
  notes: string;
  plannedStartDate: string;   // YYYY-MM-DD
  applicationCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectScheduleDetail extends ProjectSchedule {
  applications: ProjectAppRow[];
}

export interface ProjectAppRow {
  applicationId: string;
  beneficiaryName: string;
  district: string;
  mandal: string;
  village: string;
  status: string;
  plannedStartDate: string | null;
  addedAt: string | null;
}

/** Row returned by /api/project-initiation-apps (browse/picker) */
export interface AppPickerRow {
  applicationId: string;
  beneficiaryName: string;
  district: string;
  mandal: string;
  village: string;
  status: string;
  plannedStartDate: string | null;
  initiationId: string | null;
  initiationName: string | null;
}

// ── API functions ────────────────────────────────────────────────────────────

export async function fetchProjectSchedules(): Promise<ProjectSchedule[]> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/project-initiation`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<ProjectSchedule[]>;
}

export async function fetchProjectScheduleDetail(id: string): Promise<ProjectScheduleDetail> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/project-initiation/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<ProjectScheduleDetail>;
}

export async function createProjectSchedule(body: {
  name: string;
  plannedStartDate: string;
  notes?: string;
  applicationIds?: string[];
}): Promise<ProjectSchedule> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/project-initiation`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<ProjectSchedule>;
}

export async function updateProjectSchedule(
  id: string,
  body: {
    name?: string;
    plannedStartDate?: string;
    notes?: string;
    applicationIds?: string[];
  },
): Promise<ProjectSchedule> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/project-initiation/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<ProjectSchedule>;
}

export async function deleteProjectSchedule(id: string): Promise<void> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/project-initiation/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(await parseError(res));
}

export async function fetchAppPickerRows(q: {
  district?: string;
  mandal?: string;
  village?: string;
  search?: string;
  zoneGroupId?: string;
  limit?: number;
} = {}): Promise<AppPickerRow[]> {
  const params = new URLSearchParams();
  if (q.district?.trim())    params.set("district",     q.district.trim());
  if (q.mandal?.trim())      params.set("mandal",       q.mandal.trim());
  if (q.village?.trim())     params.set("village",      q.village.trim());
  if (q.search?.trim())      params.set("search",       q.search.trim());
  if (q.zoneGroupId?.trim()) params.set("zoneGroupId",  q.zoneGroupId.trim());
  if (q.limit)               params.set("limit",        String(q.limit));
  const res = await apiFetch(`${getApiBaseUrl()}/api/project-initiation-apps?${params.toString()}`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<AppPickerRow[]>;
}

// ── Zone group helpers (reuse existing geo API) ───────────────────────────────

export interface ZoneGroupOption {
  id: string;
  name: string;
  categoryId: string;
  categoryName?: string;
  /** ID of the GeoZone this group belongs to — used for beneficiary matching. */
  zoneId: string;
}

export async function fetchZoneGroupsForPicker(): Promise<ZoneGroupOption[]> {
  // Fetch categories and groups in parallel, merge category name onto each group
  const [catRes, grpRes] = await Promise.all([
    apiFetch(`${getApiBaseUrl()}/api/geo/zone-group-categories`),
    apiFetch(`${getApiBaseUrl()}/api/geo/zone-groups`),
  ]);
  if (!catRes.ok || !grpRes.ok) return [];
  const cats  = await catRes.json() as Array<{ id: string; name: string }>;
  const grps  = await grpRes.json() as Array<{ id: string; name: string; categoryId: string; zoneId: string }>;
  const catMap = new Map(cats.map((c) => [c.id, c.name]));
  return grps.map((g) => ({
    id: g.id,
    name: g.name,
    categoryId: g.categoryId,
    categoryName: catMap.get(g.categoryId) ?? "",
    zoneId: g.zoneId ?? "",
  }));
}
